import { Head, router, useForm } from '@inertiajs/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReservationLayout from '@/components/layouts/ReservationLayout'
import InterviewForm from '@/components/reservations/InterviewForm'
import PrescriptionNotice from '@/components/reservations/PrescriptionNotice'
import { useRemainingTime } from '@/hooks/useRemainingTime'
import type { ProvisionalReservation, PatientFamilyProfile } from '@/types/schedule'

type FieldOption = {
  value: string
  label: string
}

type InterviewField = {
  key: string
  label: string
  type: 'text' | 'number' | 'textarea' | 'radio' | 'checkbox_group' | 'checkbox' | 'date' | 'file'
  required: boolean
  options?: FieldOption[]
  description?: string
  conditions?: {
    sex?: string
  }
  depends_on?: string
  placeholder?: string
}

type Props = {
  provisional_reservation: ProvisionalReservation
  patient_family_profile: PatientFamilyProfile
  interview_fields: InterviewField[]
  interview_data: Record<string, string | string[]>
  errors: Record<string, string[]>
}

export default function InterviewStep2({
  provisional_reservation,
  patient_family_profile,
  interview_fields,
  interview_data,
  errors,
}: Props) {
  const { deadlineTime, isExpired } = useRemainingTime(provisional_reservation.expires_at)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasPreviousInterview, setHasPreviousInterview] = useState<boolean | null>(null)
  const [copyMessage, setCopyMessage] = useState<string | null>(null)

  // フォームの初期値を設定
  const initialFormData: Record<string, string | string[]> = {}
  interview_fields.forEach((field) => {
    if (field.type === 'checkbox_group') {
      initialFormData[field.key] = interview_data[field.key] || []
    } else if (field.type === 'checkbox') {
      initialFormData[field.key] = interview_data[field.key] || 'false'
    } else {
      initialFormData[field.key] = interview_data[field.key] || ''
    }
  })

  const { data, setData } = useForm(initialFormData)

  // 前回の問診データをキャッシュ（APIを1回だけ呼ぶ）
  const previousInterviewDataRef = useRef<Record<string, string | string[]> | null>(null)

  useEffect(() => {
    fetch(
      `/online_clinic/reservations/latest_interview_data?provisional_reservation_token=${provisional_reservation.token}`,
      {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
      }
    )
      .then((res) => res.json())
      .then((result) => {
        if (result.success && result.interview_data) {
          previousInterviewDataRef.current = result.interview_data
          setHasPreviousInterview(true)
        } else {
          setHasPreviousInterview(false)
        }
      })
      .catch(() => {
        setHasPreviousInterview(false)
      })
  }, [provisional_reservation.token])

  // 前回の問診内容をコピー（キャッシュ済みデータを使用）
  const handleCopyPrevious = useCallback(() => {
    const copiedData = previousInterviewDataRef.current
    if (!copiedData) {
      setCopyMessage('前回の問診データが見つかりませんでした。')
      return
    }

    const updates: Record<string, string | string[]> = {}
    for (const field of interview_fields) {
      const value = copiedData[field.key]
      if (value !== undefined && value !== null) {
        updates[field.key] = value
      }
    }
    setData((prev) => ({ ...prev, ...updates }))
    setCopyMessage('前回の問診内容をコピーしました。内容をご確認の上、必要に応じて修正してください。')
  }, [interview_fields, setData])

  // 依存関係をチェックして、フィールドを表示するかどうかを判定
  const shouldShowField = useCallback(
    (field: InterviewField): boolean => {
      // 条件付きフィールド（性別など）のチェック
      if (field.conditions?.sex) {
        if (field.conditions.sex !== patient_family_profile.sex) {
          return false
        }
      }

      // 依存フィールドのチェック
      if (field.depends_on) {
        const [dependsField, dependsValue] = field.depends_on.split('.')
        const currentValue = data[dependsField]

        if (Array.isArray(currentValue)) {
          return currentValue.includes(dependsValue)
        }
        return currentValue === dependsValue
      }

      return true
    },
    [data, patient_family_profile.sex]
  )

  // フィールド値の変更ハンドラ
  const handleFieldChange = useCallback(
    (key: string, value: string | string[]) => {
      setData(key, value)
    },
    [setData]
  )

  // フォーム送信
  const handleSubmit = useCallback(() => {
    setIsSubmitting(true)

    router.post(
      `/online_clinic/reservations/update_interview_step2?provisional_reservation_token=${provisional_reservation.token}`,
      { interview_step2: data },
      {
        onFinish: () => {
          setIsSubmitting(false)
        },
      }
    )
  }, [data, provisional_reservation.token])

  // 必須フィールドがすべて入力されているかチェック
  const isFormValid = useCallback((): boolean => {
    for (const field of interview_fields) {
      if (!shouldShowField(field)) continue
      if (!field.required) continue

      const value = data[field.key]
      if (field.type === 'checkbox') {
        if (value !== 'true') return false
      } else if (Array.isArray(value)) {
        if (value.length === 0) return false
      } else {
        if (!value || value.toString().trim() === '') return false
      }
    }
    return true
  }, [data, interview_fields, shouldShowField])

  // STEP1（書類アップロード）廃止により、問診の前は患者選択画面へ戻る
  const previousPageLink = `/online_clinic/reservations/select_patient?provisional_reservation_token=${provisional_reservation.token}`

  return (
    <>
      <Head title="問診入力 - オンライン診療予約" />

      <ReservationLayout
        currentStep="interview2"
        previousPageLink={previousPageLink}
        title="問診入力"
        expiresAt={provisional_reservation.expires_at}
      >
        <div className="space-y-6">
          {/* 診療TOPから受診者を決めて入った場合は受診者選択画面を通らないため、
              誰の受診かをここで目立つ形で示し、取り違えに気づけるようにする(#1060) */}
          <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3">
            <p className="text-sm text-[#515862]">
              <span className="font-semibold text-[#2a3039]">{patient_family_profile.full_name}</span>
              さんの診療について伺います
            </p>
          </div>

          {/* 仮予約情報 */}
          <div className="oc-info-card">
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-base font-semibold text-[#2a3039]">仮予約中の診療枠</h3>
              <div className="text-sm">
                <span className="text-[#6f7680]">確保期限: </span>
                <span className={`font-medium ${isExpired ? 'text-red-600' : 'text-cyan-600'}`}>
                  {isExpired ? '期限切れ' : `${deadlineTime}まで`}
                </span>
              </div>
            </div>
            <div className="space-y-1 text-sm text-[#515862]">
              <p>
                <span className="text-[#6f7680]">患者: </span>
                {patient_family_profile.full_name}
              </p>
              <p>
                <span className="text-[#6f7680]">診療科目: </span>
                {provisional_reservation.medical_department.name}
              </p>
              <p>
                <span className="text-[#6f7680]">日時: </span>
                {provisional_reservation.doctor_schedule.formatted_date}{' '}
                {provisional_reservation.doctor_schedule.formatted_time}
              </p>
              <p>
                <span className="text-[#6f7680]">担当医師: </span>
                {provisional_reservation.doctor_schedule.doctor.full_name}
              </p>
              <PrescriptionNotice
                nightSlot={provisional_reservation.doctor_schedule.night_slot}
                expectedPrescriptionIssuedDate={provisional_reservation.doctor_schedule.expected_prescription_issued_date}
                pharmacyReceivableFrom={provisional_reservation.doctor_schedule.pharmacy_receivable_from}
                className="mt-1"
              />
            </div>
          </div>

          {/* 問診フォーム */}
          <div className="oc-card">
            <h2 className="oc-section-title">問診入力</h2>
            <p className="mb-6 text-sm text-[#6f7680]">
              診察に必要な情報を入力してください。各項目の「必須」バッジをご確認ください。
            </p>

            {/* 前回の問診内容コピーボタン */}
            {hasPreviousInterview && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={handleCopyPrevious}
                  className="w-full rounded-lg border border-cyan-300 bg-cyan-50 px-4 py-3 text-sm font-medium text-cyan-700 transition-colors hover:bg-cyan-100"
                >
                  前回の問診内容をコピーする
                </button>
              </div>
            )}

            {/* コピー結果メッセージ */}
            {copyMessage && (
              <div className="mb-4 rounded-lg border border-cyan-200 bg-cyan-50 p-4">
                <p className="text-sm text-cyan-700">{copyMessage}</p>
              </div>
            )}

            {/* エラー表示 */}
            {Object.keys(errors).length > 0 && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
                <ul className="text-sm text-red-600 list-disc list-inside">
                  {Object.entries(errors).map(([key, messages]) =>
                    messages.map((msg, i) => <li key={`${key}-${i}`}>{msg}</li>)
                  )}
                </ul>
              </div>
            )}

            <InterviewForm
              fields={interview_fields}
              values={data}
              errors={errors}
              onChange={handleFieldChange}
              shouldShowField={shouldShowField}
              provisionalReservationToken={provisional_reservation.token}
            />
          </div>
        </div>

        {/* ナビゲーションボタン */}
        <div className="oc-action-bar">
          <div className="oc-action-container">
            <button
              type="button"
              onClick={() => router.visit(previousPageLink)}
              className="button-secondary"
            >
              前へ
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="button-primary"
            >
              {isSubmitting ? '処理中...' : '次へ'}
            </button>
          </div>
        </div>

        {/* ボタン分のスペース確保 */}
        <div className="h-24" />
      </ReservationLayout>
    </>
  )
}
